import React, { useState, useEffect } from "react";

const BodyFatCalculator = () => {
  const [age, setAge] = useState(25);
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [gender, setGender] = useState("male");
  const [neck, setNeck] = useState(37);
  const [waist, setWaist] = useState(80);
  const [hip, setHip] = useState(95);
  const [unitSystem, setUnitSystem] = useState("metric");
  const [result, setResult] = useState({
    bodyFat: "0",
    category: "",
    interpretation: "",
    advice: "",
    leanMass: "",
    fatMass: "",
  });

  // Body Fat Percentage Calculation
  const calculateBodyFat = () => {
    let w = parseFloat(weight);
    let h = parseFloat(height);
    let a = parseFloat(age);
    let n = parseFloat(neck);
    let wa = parseFloat(waist);
    let hi = parseFloat(hip);

    if (isNaN(w) || w <= 0 || isNaN(h) || h <= 0 || isNaN(a) || a <= 0 || isNaN(n) || n <= 0 || isNaN(wa) || wa <= 0) return;
    if (gender === "female" && (isNaN(hi) || hi <= 0)) return;

    // Convert to metric if needed
    let heightCm = unitSystem === "metric" ? h : h * 2.54;
    let weightKg = unitSystem === "metric" ? w : w * 0.453592;
    let neckCm = unitSystem === "metric" ? n : n * 2.54;
    let waistCm = unitSystem === "metric" ? wa : wa * 2.54;
    let hipCm = unitSystem === "metric" ? hi : hi * 2.54;

    // US Navy Method
    let bodyFatPercentage;
    
    if (gender === "male") {
      // Male formula: 495 / (1.0324 - 0.19077 × log10(waist - neck) + 0.15456 × log10(height)) - 450
      bodyFatPercentage = 495 / (1.0324 - 0.19077 * Math.log10(waistCm - neckCm) + 0.15456 * Math.log10(heightCm)) - 450;
    } else {
      // Female formula: 495 / (1.29579 - 0.35004 × log10(waist + hip - neck) + 0.22100 × log10(height)) - 450
      bodyFatPercentage = 495 / (1.29579 - 0.35004 * Math.log10(waistCm + hipCm - neckCm) + 0.221 * Math.log10(heightCm)) - 450;
    }

    // Ensure reasonable range
    bodyFatPercentage = Math.max(3, Math.min(60, bodyFatPercentage));

    // Calculate fat mass and lean mass
    const fatMass = (bodyFatPercentage / 100) * weightKg;
    const leanMass = weightKg - fatMass;

    // Determine category based on age and gender
    let category = "";
    let interpretation = "";
    
    if (gender === "male") {
      if (bodyFatPercentage < 6) {
        category = "Essential Fat";
        interpretation = "This is below the essential fat level. Essential fat is necessary for basic physiological function. Consult a healthcare provider.";
      } else if (bodyFatPercentage < 14) {
        category = "Athletes";
        interpretation = "You're in the athletic range. This level is typical for competitive athletes and requires dedicated training and nutrition.";
      } else if (bodyFatPercentage < 18) {
        category = "Fitness";
        interpretation = "You're in the fitness range. This is considered healthy and achievable for people who exercise regularly.";
      } else if (bodyFatPercentage < 25) {
        category = "Average";
        interpretation = "You're in the average range. This is typical for the general population but there's room for improvement through exercise and diet.";
      } else {
        category = "Obese";
        interpretation = "You're in the obese range. Consider consulting with healthcare professionals for a comprehensive weight management plan.";
      }
    } else {
      if (bodyFatPercentage < 14) {
        category = "Essential Fat";
        interpretation = "This is at or below essential fat levels. Women naturally require more essential fat. Consult a healthcare provider.";
      } else if (bodyFatPercentage < 21) {
        category = "Athletes";
        interpretation = "You're in the athletic range. This level is typical for competitive female athletes and requires dedicated training.";
      } else if (bodyFatPercentage < 25) {
        category = "Fitness";
        interpretation = "You're in the fitness range. This is considered healthy and achievable for women who exercise regularly.";
      } else if (bodyFatPercentage < 32) {
        category = "Average";
        interpretation = "You're in the average range. This is typical for women in the general population but there's room for improvement.";
      } else {
        category = "Obese";
        interpretation = "You're in the obese range. Consider consulting with healthcare professionals for a comprehensive weight management plan.";
      }
    }

    // Generate personalized advice
    let advice = "";
    if (bodyFatPercentage < 10 && gender === "male" || bodyFatPercentage < 18 && gender === "female") {
      advice = "You have low body fat. Focus on maintaining muscle mass through strength training and ensure adequate nutrition for hormonal health and energy levels.";
    } else if (bodyFatPercentage < 20 && gender === "male" || bodyFatPercentage < 28 && gender === "female") {
      advice = "You're at a healthy body fat percentage. Maintain your current fitness routine, focus on balanced nutrition, and ensure adequate protein intake for muscle preservation.";
    } else {
      advice = "To reduce body fat, combine regular cardiovascular exercise with strength training, maintain a moderate caloric deficit, prioritize protein intake (0.8-1g per pound), and ensure quality sleep.";
    }

    const fatMassDisplay = unitSystem === "metric" 
      ? `${fatMass.toFixed(1)}kg`
      : `${(fatMass * 2.20462).toFixed(1)}lbs`;
    
    const leanMassDisplay = unitSystem === "metric" 
      ? `${leanMass.toFixed(1)}kg`
      : `${(leanMass * 2.20462).toFixed(1)}lbs`;

    setResult({
      bodyFat: `${bodyFatPercentage.toFixed(1)}%`,
      category,
      interpretation,
      advice,
      leanMass: leanMassDisplay,
      fatMass: fatMassDisplay,
    });
  };

  useEffect(() => {
  calculateBodyFat();
}, [age, weight, height, gender, neck, waist, hip, unitSystem]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
          </div>
          <h1>Body Fat Percentage Calculator</h1>
          <p>
            Calculate your body fat percentage using the US Navy method - a scientifically validated formula that uses circumference measurements to estimate body composition accurately.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Your Measurements
              </h2>

              {/* Age Input */}
              <div className="input-group">
                <label>Age (years)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                    max="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[20, 30, 40, 50, 60].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAge(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight Input */}
              <div className="input-group">
                <label>Weight ({unitSystem === "metric" ? "kg" : "lbs"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⚖️</span>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 60, 70, 80, 90].map((val) => (
                    <button
                      key={val}
                      onClick={() => setWeight(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height Input */}
              <div className="input-group">
                <label>Height ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📏</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="calc-input"
                    placeholder="170"
                  />
                </div>
                <div className="quick-select-group">
                  {[150, 160, 170, 180, 190].map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeight(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Neck Circumference */}
              <div className="input-group">
                <label>Neck Circumference ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎯</span>
                  <input
                    type="number"
                    value={neck}
                    onChange={(e) => setNeck(e.target.value)}
                    className="calc-input"
                    placeholder="37"
                  />
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Measure around your neck at the narrowest point, just below the Adam's apple.
                </p>
              </div>

              {/* Waist Circumference */}
              <div className="input-group">
                <label>Waist Circumference ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📐</span>
                  <input
                    type="number"
                    value={waist}
                    onChange={(e) => setWaist(e.target.value)}
                    className="calc-input"
                    placeholder="80"
                  />
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Measure at the narrowest point, typically just above the belly button. Keep the tape horizontal.
                </p>
              </div>

              {/* Hip Circumference (Women only) */}
              {gender === "female" && (
                <div className="input-group">
                  <label>Hip Circumference ({unitSystem === "metric" ? "cm" : "in"})</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">📏</span>
                    <input
                      type="number"
                      value={hip}
                      onChange={(e) => setHip(e.target.value)}
                      className="calc-input"
                      placeholder="95"
                    />
                  </div>
                  <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                    Measure around the widest part of your hips and buttocks.
                  </p>
                </div>
              )}

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unit System */}
              <div className="input-group">
                <label>Measurement System</label>
                <div className="quick-select-group">
                  {[
                    { value: "metric", label: "Metric (kg/cm)" },
                    { value: "imperial", label: "Imperial (lbs/in)" },
                  ].map((unit) => (
                    <button
                      key={unit.value}
                      onClick={() => setUnitSystem(unit.value)}
                      className={`quick-select-btn ${unitSystem === unit.value ? "primary" : "secondary"}`}
                    >
                      {unit.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateBodyFat} className="calculate-btn">
                Calculate Body Fat Percentage
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Your Body Composition
              </h2>

              <div className="result-card">
                {/* Body Fat Percentage */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Body Fat Percentage</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.bodyFat}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Body Composition Breakdown */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Fat Mass</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.fatMass}</div>
                    <div className="result-subtext">adipose tissue</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Lean Mass</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5.5 8.5 9 12l-3.5 3.5L2 12l3.5-3.5z"></path>
                          <path d="m12 2 3.5 3.5L12 9 8.5 5.5 12 2z"></path>
                          <path d="M18.5 8.5 22 12l-3.5 3.5L15 12l3.5-3.5z"></path>
                          <path d="m12 15 3.5 3.5L12 22l-3.5-3.5L12 15z"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.leanMass}</div>
                    <div className="result-subtext">muscle & organs</div>
                  </div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">What This Means</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500", marginBottom: "0.75rem" }}>
                    {result.interpretation}
                  </p>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.advice}
                  </p>
                </div>

                {/* Body Fat Categories Chart */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">Body Fat Categories</h3>
                  {gender === "male" ? (
                    <>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Essential Fat: 2-5%</div>
                        <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Minimum for basic physiological function</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dcfce7", borderRadius: "0.375rem", borderLeft: "4px solid #10b981" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Athletes: 6-13%</div>
                        <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Typical for competitive athletes</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#d1fae5", borderRadius: "0.375rem", borderLeft: "4px solid #34d399" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Fitness: 14-17%</div>
                        <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Healthy, fit appearance</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Average: 18-24%</div>
                        <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Typical for general population</div>
                      </div>
                      <div style={{ padding: "0.75rem", backgroundColor: "#fee2e2", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Obese: 25%+</div>
                        <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Health risks increase</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Essential Fat: 10-13%</div>
                        <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Minimum for basic physiological function</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dcfce7", borderRadius: "0.375rem", borderLeft: "4px solid #10b981" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Athletes: 14-20%</div>
                        <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Typical for competitive athletes</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#d1fae5", borderRadius: "0.375rem", borderLeft: "4px solid #34d399" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Fitness: 21-24%</div>
                        <div style={{ fontSize: "0.75rem", color: "#065f46" }}>Healthy, fit appearance</div>
                      </div>
                      <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Average: 25-31%</div>
                        <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Typical for general population</div>
                      </div>
                      <div style={{ padding: "0.75rem", backgroundColor: "#fee2e2", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                        <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Obese: 32%+</div>
                        <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Health risks increase</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Body Fat Percentage</h3>
              <p>
                Body fat percentage represents the proportion of your body weight that is fat tissue versus lean tissue (muscle, bone, organs, water). This calculator uses the US Navy Method, developed by the Department of Defense.
                This method uses circumference measurements to estimate body fat and has been validated against underwater weighing and DEXA scans. It's more accurate than BMI because it accounts for body composition rather than just height and weight.
                The formula considers that men and women naturally store fat differently, with women requiring higher essential fat levels for reproductive and hormonal health.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>US Navy Method Formulas:</h4>
            <div className="formula-display">
              Men: 495 / (1.0324 - 0.19077 × log(waist - neck) + 0.15456 × log(height)) - 450
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Women: 495 / (1.29579 - 0.35004 × log(waist + hip - neck) + 0.221 × log(height)) - 450
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              All measurements are in centimeters for consistency and accuracy
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Neck</div>
                <div className="formula-param-value">Below Adam's apple</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Waist</div>
                <div className="formula-param-value">Narrowest point</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Hip (Women)</div>
                <div className="formula-param-value">Widest point</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Accuracy</div>
                <div className="formula-param-value">±3-4% margin</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How accurate is the US Navy Method?</h5>
              <p>
                The US Navy Method has a margin of error of ±3-4%. While not as precise as DEXA scans or hydrostatic weighing, it's practical, free, and reasonably accurate for tracking changes over time.
              </p>
            </div>

            <div className="faq-item">
              <h5>How should I measure my circumferences?</h5>
              <p>
                Use a flexible measuring tape. Measure in the morning before eating. Keep the tape snug but not tight. Take 2-3 measurements and use the average. Measure at the exact same spots each time for consistency.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's a healthy body fat percentage?</h5>
              <p>
                For men, 10-20% is generally healthy. For women, 18-28% is healthy. Athletes may have lower percentages. Essential fat (needed for bodily function) is 2-5% for men and 10-13% for women.
              </p>
            </div>

            <div className="faq-item">
              <h5>Is body fat percentage more important than weight?</h5>
              <p>
                Yes! Body composition (fat vs. muscle) is more important than scale weight. Two people of the same weight can look very different based on their body fat percentage. Muscle is denser than fat.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I reduce body fat percentage?</h5>
              <p>
                Combine strength training (to build/preserve muscle), cardiovascular exercise, a moderate caloric deficit, high protein intake (0.8-1g per pound of body weight), quality sleep, and stress management.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why do women need higher body fat than men?</h5>
              <p>
                Women require more essential fat for reproductive function, hormone production, and overall health. Female hormones require adequate fat stores. Going too low can disrupt menstrual cycles and bone health.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BodyFatCalculator;